import React, { useEffect, useState } from 'react';
import Modal from '../shared/Modal';
import { TextField, SelectField, SegmentedField } from '../shared/FormField';
import { GENETIC_TYPES, PRIMARY_PRODUCTION_TRAITS } from '../data/enums';
import { buildSpeciesOptions, parseApiError } from '../FoundationalTables/SpeciesLivestockRegistry/registryHelpers';

export default function EditBreedEcotypeModal({ isOpen, onClose, onSave, row, species = [] }) {
  const [form, setForm] = useState({
    species_uuid: '',
    breed_code: '',
    breed_name: '',
    genetic_type: '',
    primary_production_trait: '',
  });
  const [status, setStatus] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));

  useEffect(() => {
    if (!row) return;

    setForm({
      species_uuid: row.species_uuid ?? '',
      breed_code: row.breed_code ?? '',
      breed_name: row.breed_name ?? '',
      genetic_type: row.genetic_type ?? '',
      primary_production_trait: row.primary_production_trait ?? '',
    });
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
  }, [row]);

  const speciesOptions = buildSpeciesOptions(species);

  const submit = async () => {
    if (submitting || !row?.uuid) return;

    if (
      !form.species_uuid ||
      !form.breed_code.trim() ||
      !form.breed_name.trim() ||
      !form.genetic_type ||
      !form.primary_production_trait
    ) {
      setErrMsg('Please complete the required fields before saving.');
      setStatus('error');
      return;
    }

    setSubmitting(true);
    setStatus('idle');
    setErrMsg('');

    try {
      await onSave({
        uuid: row.uuid,
        species_uuid: form.species_uuid,
        breed_code: form.breed_code.trim(),
        breed_name: form.breed_name.trim(),
        genetic_type: form.genetic_type,
        primary_production_trait: form.primary_production_trait,
      });
      setStatus('success');
    } catch (error) {
      setErrMsg(parseApiError(error, 'Unable to update breed record.'));
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setErrMsg('');
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal
      title="Edit Breed / Ecotype"
      isOpen={isOpen}
      onClose={handleClose}
      onSubmit={submit}
      status={status}
      errorMessage={errMsg}
      onContinue={handleClose}
      submitLabel="Save Changes"
      successTitle="Breed Updated"
      successMessage="The breed changes were saved to the backend."
      isSubmitting={submitting}
    >
      <TextField
        label="Breed Code"
        required
        value={form.breed_code}
        onChange={set('breed_code')}
        readOnly
        className="cursor-not-allowed bg-gray-50 font-mono"
        note="System identifier - cannot be edited after creation."
      />
      <SelectField
        label="Species"
        required
        value={form.species_uuid}
        onChange={set('species_uuid')}
        options={speciesOptions}
        placeholder="Select Species"
      />
      <TextField
        label="Breed Name"
        required
        value={form.breed_name}
        onChange={set('breed_name')}
        placeholder="e.g. Bunaji (White Fulani)"
      />
      <SegmentedField
        label="Genetic Type"
        required
        value={form.genetic_type}
        onChange={set('genetic_type')}
        options={GENETIC_TYPES}
      />
      <SegmentedField
        label="Primary Production Trait"
        required
        value={form.primary_production_trait}
        onChange={set('primary_production_trait')}
        options={PRIMARY_PRODUCTION_TRAITS}
      />
    </Modal>
  );
}
